import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import SuspenseSpinner from 'components/SuspenseFallback'
import { useApplicationContext } from '../context/ApplicationContext'
import { getCurrentEnvironment } from '../utils/session-utils'
import { UserUnauthorized } from '../components/common/user-unauthorized'
import DeprecatedRoutes from '../utils/DeprecatedRoutes'

const Account = lazy(() => import('../pages/account'))
const ErrorLogFailures = lazy(() => import('../pages/api-webhooks-errors'))
const WebhooksEvents = lazy(() => import('../pages/webhooks-events'))
const APICalls = lazy(() => import('../pages/dashboard'))
const Help = lazy(() => import('../pages/help'))
const DashboardHome = lazy(() => import('../pages/home'))
const Applications = lazy(() => import('../pages/applications'))
const CreateApplication = lazy(() => import('../pages/applications/create'))
const ApplicationDetails = lazy(() => import('../pages/applications/edit'))
const IpnSimulator = lazy(() => import('../pages/ipn'))
const NotFound = lazy(() => import('../pages/notfound'))
const BTCredDetailsLive = lazy(() =>
  import('../pages/applications/editbtcred/editbtcredLive'),
)
const BTCredDetailsSandbox = lazy(() =>
  import('../pages/applications/editbtcred/editbtcredSandbox'),
)
const CreditCardGenerator = lazy(() =>
  import('../pages/mock/creditCardGenerator'),
)
const WebhookSimulator = lazy(() => import('../pages/mock/webhookSimulator'))
const SandboxAccount = lazy(() => import('../pages/sandbox-account'))
const SandboxAccountDetails = lazy(() =>
  import('../pages/sandbox-account/edit'),
)
const SandboxCreateAccount = lazy(() =>
  import('../pages/sandbox-account/create'),
)
const SandboxCreateCustomAccount = lazy(() =>
  import('../pages/sandbox-account/create-custom'),
)
const SandboxBulkCreateA = lazy(() => import('../pages/sandbox-account/bulk'))
const Notifications = lazy(() => import('../pages/notifications'))
const Transactions = lazy(() => import('../pages/transactions'))

function SignInPage() {
  const { user, contextualLoginUrl } = useApplicationContext()
  if (user) {
    if (user.unauthorized === true) {
      return <UserUnauthorized />
    }
    return <Outlet />
  }
  window.location.href = contextualLoginUrl
  return null
}

function AuthRequired() {
  return (
    <>
      <SignInPage />
    </>
  )
}

const AppRoutes = () => {
  const { user } = useApplicationContext()
  return (
    <Suspense fallback={<SuspenseSpinner />}>
      <Routes>
        <Route path="/" element={<AuthRequired />}>
          <Route exact path="/" element={<DashboardHome />} />
          <Route
            exact
            path="/applications"
            element={
              <Navigate
                to={'/applications/' + getCurrentEnvironment(user)}
                replace
              />
            }
          />
          <Route
            element={
              <DeprecatedRoutes
                newPath="/applications/sandbox"
                params={{ openBTCredModal: true }}
              />
            }
          >
            <Route path="/applications/createbtcred" element={<></>} />
          </Route>
          <Route
            element={
              <DeprecatedRoutes
                newPath="/applications/live"
                params={{ openBTCredModal: true }}
              />
            }
          >
            <Route path="/applications/createbtcredlive" element={<></>} />
          </Route>
          <Route index path="/applications/:env" element={<Applications />} />
          <Route
            path="/applications/edit/:id"
            element={<ApplicationDetails />}
          />
          <Route
            index
            path="/applications/:env/create"
            element={<CreateApplication />}
          />
          <Route
            path="/applications/braintreeCredentials/editbtcredLive"
            element={<BTCredDetailsLive />}
          />
          <Route
            path="/applications/braintreeCredentials/editbtcred"
            element={<BTCredDetailsSandbox />}
          />
          <Route path="/accounts" element={<SandboxAccount />} />
          <Route
            path="/accounts/edit/:id"
            element={<SandboxAccountDetails />}
          />
          <Route element={<DeprecatedRoutes newPath="/accounts" />}>
            <Route path="/accounts/create" element={<SandboxCreateAccount />} />
            <Route
              path="/accounts/create/custom"
              element={<SandboxCreateCustomAccount />}
            />
            <Route path="/accounts/bulk" element={<SandboxBulkCreateA />} />
          </Route>
          <Route
            path="/creditCardGenerator"
            element={<CreditCardGenerator />}
          />
          <Route
            path="/dashboard/sandbox"
            element={<APICalls environment={'sandbox'} />}
          />
          <Route
            path="/dashboard/live"
            element={<APICalls environment={'live'} />}
          />
          <Route
            path="/webhooks/live"
            element={<WebhooksEvents environment={'live'} />}
          />
          <Route
            path="/webhooks/sandbox"
            element={<WebhooksEvents environment={'sandbox'} />}
          />
          <Route path="/webhooksSimulator" element={<WebhookSimulator />} />
          <Route path="/ipnSimulator" element={<IpnSimulator />} />
          <Route path="/notifications/" element={<Notifications />} />
          <Route
            path="/errors/sandbox"
            element={<ErrorLogFailures environment={'sandbox'} />}
          />
          <Route
            path="/errors/live"
            element={<ErrorLogFailures environment={'live'} />}
          />
          <Route
            path="/notifications/recent/:accountid"
            element={<Notifications />}
          />
          <Route path="/accountStatus" element={<Account />} />
          <Route path="/help" element={<Help />} />
          <Route path="/transactions/:env" element={<Transactions />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
